import { apiGet, buildQueryParams, type PageResult } from '~/utils/api'

/**
 * Shop product response (matches backend ShopProductResponse)
 */
export interface ShopProduct {
  id: string
  productId: string
  productName: string
  categoryCode: string
  brandCode: string
  /** Manufacturer model code (e.g. "A2893"); used as schema.org Product mpn. */
  modelCode: string | null
  title: string
  slug: string | null
  description: string
  storage: string | null
  color: string | null
  conditionGrade: string
  originalPrice: number
  salePrice: number
  displayFromPrice: number
  inventoryCount: number
  /** 'DEVICE' (refurbished per-unit) or 'ACCESSORY' (fungible counted stock). */
  productType: 'DEVICE' | 'ACCESSORY'
  /** Counted stock for accessories; null for devices (use `inventoryCount`). */
  stockQuantity: number | null
  accessoryCategoryCode: string | null
  accessoryCategoryName: string | null
  /** Shipping fee for this product; null or 0 means free shipping. */
  shippingFee: number | null
  /** Order subtotal needed to ship this item free; null means no such threshold. */
  shippingFreeThreshold: number | null
  images: string[]
  titleTranslations: Record<string, string> | null
  descriptionTranslations: Record<string, string> | null
  createdAt: string
  activeWeeklyDeal: {
    id: string
    targetType: 'PRODUCT' | 'INVENTORY_ITEM'
    inventoryItemId: string | null
    regularPrice: number
    campaignPrice: number
    startsAt: string
    endsAt: string
  } | null
}

/**
 * Accessory category (storefront filter; matches backend response)
 */
export interface AccessoryCategory {
  code: string
  name: string
  icon: string | null
  nameTranslations: Record<string, string> | null
}

/** Device category enabled for the sales business. */
export interface ShopCategory {
  id: string
  code: string
  name: string
  icon: string | null
  brandId: string
  brandCode: string
  sortOrder: number
  nameTranslations: Record<string, string> | null
}

/**
 * Shop product query parameters
 */
export interface ShopProductQuery {
  categoryCode?: string
  /** Comma-separated category codes (用于聚合分类筛选,如前台 Android 虚拟分组) */
  categoryCodes?: string
  brandCode?: string
  conditionGrade?: string
  /** 'DEVICE' | 'ACCESSORY' */
  productType?: 'DEVICE' | 'ACCESSORY'
  accessoryCategoryCode?: string
  minPrice?: number
  maxPrice?: number
  q?: string
  sort?: string
  page?: number
  size?: number
}

/**
 * Product inventory response - grouped by condition grade
 */
export interface DeviceSummary {
  id: string
  regularPrice: number
  salePrice: number
  weeklyDealApplied: boolean
  batteryHealth: number | null
  storage: string | null
  color: string | null
  warrantyExpiresAt: string | null
  warrantyMonths: number | null
  images: string[]
}

export interface ConditionGroup {
  conditionGrade: string
  count: number
  fromPrice: number
  devices: DeviceSummary[]
}

export interface ProductInventoryResponse {
  conditions: ConditionGroup[]
}

/**
 * Shop API composable
 */
export function useShopAPI() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Get shop products with pagination and filtering
   */
  async function fetchProducts(query: ShopProductQuery = {}): Promise<PageResult<ShopProduct>> {
    loading.value = true
    error.value = null

    try {
      const params: Record<string, any> = {
        page: query.page ?? 0,
        size: query.size ?? 20,
      }

      if (query.categoryCode) params.categoryCode = query.categoryCode
      if (query.categoryCodes) params.categoryCodes = query.categoryCodes
      if (query.brandCode) params.brandCode = query.brandCode
      if (query.conditionGrade) params.conditionGrade = query.conditionGrade
      if (query.productType) params.productType = query.productType
      if (query.accessoryCategoryCode) params.accessoryCategoryCode = query.accessoryCategoryCode
      if (query.minPrice !== undefined) params.minPrice = query.minPrice
      if (query.maxPrice !== undefined) params.maxPrice = query.maxPrice
      if (query.q) params.q = query.q
      if (query.sort) params.sort = query.sort

      const queryString = buildQueryParams(params).toString()
      return await apiGet<PageResult<ShopProduct>>(`/api/app/v1/shop/products?${queryString}`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a single shop product by ID
   */
  async function fetchProduct(productId: string): Promise<ShopProduct> {
    loading.value = true
    error.value = null

    try {
      return await apiGet<ShopProduct>(`/api/app/v1/shop/products/${productId}`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Get a single shop product by slug
   * (requires backend slug support - Phase 4.2)
   */
  async function fetchProductBySlug(slug: string): Promise<ShopProduct> {
    loading.value = true
    error.value = null

    try {
      return await apiGet<ShopProduct>(`/api/app/v1/shop/products/by-slug/${slug}`)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Get product inventory grouped by condition grade
   */
  async function fetchProductInventory(productId: string): Promise<ProductInventoryResponse> {
    // Inventory is an optional enrichment on the product page. Its caller owns
    // the local warning state, so a failure must not poison the shared product
    // fetch error and replace a valid product with the global "not found" UI.
    return await apiGet<ProductInventoryResponse>(`/api/app/v1/shop/products/${productId}/inventory`)
  }

  /**
   * Get active accessory categories for the storefront filter
   */
  async function fetchAccessoryCategories(): Promise<AccessoryCategory[]> {
    try {
      return await apiGet<AccessoryCategory[]>('/api/app/v1/shop/accessory-categories')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    }
  }

  /** Get active device categories enabled for the shop. */
  async function fetchDeviceCategories(): Promise<ShopCategory[]> {
    try {
      return await apiGet<ShopCategory[]>('/api/app/v1/shop/categories')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    }
  }

  return {
    loading,
    error,
    fetchProducts,
    fetchProduct,
    fetchProductBySlug,
    fetchProductInventory,
    fetchDeviceCategories,
    fetchAccessoryCategories,
  }
}
